import { useLocation } from "react-router-dom";
import {
    isReportingNavRoute,
    REPORTING_ROUTE,
} from "@components/reporting/reportingRoutes";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { GraphIcon, GrowthIcon } from "@icons";
import { REPORTING_SCOPE_QUERY_PARAM } from "@components/reporting/reportingScope";
import { isCatalogLinkActive } from "./isCatalogLinkActive";
import { ConsolidationGroupRow, EntityReportsRow } from "./ReportsEntityRows";
import { useNavigationShortcutKeys } from "./useNavigationShortcutKeys";
import { ACCOUNTING_REPORTS_PATH } from "./workspaceSideNavSections";
import type { SideNavSectionState } from "./sideNavSection.types";

/**
 * Consolidation group, business entities, Insights and All reports. The
 * first entity starts expanded when it is a business.
 */
export const ReportsSection = ({
    section,
    canUseConsolidation,
    hasBusinessEntities,
}: {
    section: SideNavSectionState;
    canUseConsolidation: boolean;
    hasBusinessEntities: boolean;
}) => {
    const { pathname, search } = useLocation();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { consolidation: isReportingEnabled } = useTypedFlags();
    const entities = useEntities();
    const shortcutKeys = useNavigationShortcutKeys();
    const businessEntities = entities.filter((entity) => !entity.isPersonal);
    const firstEntity = entities[0];
    const allReportsPath = isReportingEnabled
        ? REPORTING_ROUTE
        : ACCOUNTING_REPORTS_PATH;
    const isAllReportsActive = isReportingEnabled
        ? isReportingNavRoute(pathname) &&
          !new URLSearchParams(search).has(REPORTING_SCOPE_QUERY_PARAM)
        : isCatalogLinkActive({ to: allReportsPath, pathname, search });

    if (isGlFirst && businessEntities.length === 0) {
        return null;
    }

    return (
        <SideNavSection {...section}>
            {canUseConsolidation && <ConsolidationGroupRow />}
            {businessEntities.map((entity) => (
                <EntityReportsRow
                    key={entity.id}
                    entity={entity}
                    isExpandedByDefault={
                        entity === firstEntity && firstEntity.isBusiness
                    }
                />
            ))}
            {!isGlFirst && (
                <SideNavItem
                    to="/insights"
                    end
                    icon={<GraphIcon />}
                    label="Insights"
                    shortcut={shortcutKeys("/insights")}
                />
            )}
            {hasBusinessEntities && (
                <SideNavItem
                    to={allReportsPath}
                    isActive={isAllReportsActive}
                    icon={<GrowthIcon />}
                    label="All reports"
                    shortcut={shortcutKeys(ACCOUNTING_REPORTS_PATH)}
                />
            )}
        </SideNavSection>
    );
};
